import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { EditorSDK } from '../sdk';
import type { CommunicationLayer } from '../services/communication';
import type { Metadata } from '../types';

export interface UseEditorSdkProps {
  editorSdkId: string;
  communicationLayer: CommunicationLayer;
  draft?: EditorSDK['draft'];
  modifications?: EditorSDK['modifications'];
  api?: EditorSDK['api'];
  mode: EditorSDK['mode'];
  iframeId?: EditorSDK['iframeId'];
  userData: EditorSDK['userData'];
  organizationData: EditorSDK['organizationData'];
  filesServiceDomain: EditorSDK['filesServiceDomain'];
  recordingType?: EditorSDK['recordingType'];
  onReceivedMetadata?: (metadata: Metadata) => void;
}

export const useEditorSdk = ({
  editorSdkId,
  communicationLayer,
  draft,
  modifications,
  api,
  mode,
  iframeId,
  userData,
  organizationData,
  filesServiceDomain,
  recordingType = 'cloner',
  onReceivedMetadata,
}: UseEditorSdkProps) => {
  const flags = useFlags();
  const editorSdk = useMemo(
    () =>
      new EditorSDK({
        id: editorSdkId,
        communicationLayer,
      }),
    []
  );

  useEffect(() => {
    editorSdk.setRecordingType(recordingType);
  }, [recordingType, editorSdk]);

  useEffect(() => {
    editorSdk.setFilesServiceDomain(filesServiceDomain);
  }, [filesServiceDomain, editorSdk]);

  useEffect(() => {
    editorSdk.setFlags(flags);
  }, [flags, editorSdk]);

  useEffect(() => {
    if (userData) {
      editorSdk.setUserData(userData);
    }
  }, [userData, editorSdk]);

  useEffect(() => {
    if (organizationData) {
      editorSdk.setOrganizationData(organizationData);
    }
  }, [organizationData, editorSdk]);

  useEffect(() => {
    editorSdk.setMode(mode);
  }, [mode, editorSdk]);

  useEffect(() => {
    if (iframeId) {
      editorSdk.setIframeId(iframeId);
    }
  }, [iframeId, editorSdk]);

  useEffect(() => {
    if (draft) {
      editorSdk.setDraft(draft);
    }
  }, [draft, editorSdk]);

  useEffect(() => {
    if (modifications) {
      editorSdk.setModifications(modifications);
    }
  }, [modifications, editorSdk]);

  useEffect(() => {
    if (onReceivedMetadata) {
      const unsubscribe = editorSdk.subscribe(
        'updateMetadata',
        onReceivedMetadata
      );
      return () => unsubscribe();
    }
  }, [onReceivedMetadata, editorSdk]);

  useEffect(() => {
    if (editorSdk) {
      editorSdk.externalAPI = api;
    }
  }, [api, editorSdk]);

  useEffect(() => {
    editorSdk.init();
    return () => {
      editorSdk.destroy();
    };
  }, [editorSdk]);

  return editorSdk;
};
